import React, { useState, useCallback } from "react";
import { message, Spin } from "antd";
import {
  InboxOutlined,
  RobotOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";

const ResumeUpload = ({ onParsed, onSkip }) => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "text/plain"];
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!allowed.includes(f.type) && !["pdf", "doc", "docx", "txt"].includes(ext)) {
      message.error("Please upload a PDF, DOC, DOCX, or TXT file");
      return;
    }
    setFile(f);
    setParsed(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleBrowse = (e) => {
    const f = e.target.files[0];
    if (f) handleFile(f);
    e.target.value = "";
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const token = localStorage.getItem("authToken");
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(API_CONST.PARSE_RESUME, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setParsed(true);
      message.success("Resume parsed! Form fields have been pre-filled — review and edit as needed.");
      onParsed(res.data.parsed, file);
    } catch (err) {
      message.error(err.response?.data?.message || "AI parsing failed. You can still fill the form manually.");
    } finally {
      setParsing(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(API_CONST.RESUME_SAMPLE_TEMPLATE, "_blank");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "340px", gap: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 520 }}>
        <div style={{ width: 56, height: 56, background: "linear-gradient(135deg, #01d9a9, #014c75)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: "0 8px 24px rgba(1,217,169,0.25)" }}>
          <FileTextOutlined style={{ fontSize: 26, color: "white" }} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#014c75", margin: "0 0 6px" }}>Upload Resume</h2>
        <p style={{ fontSize: 14, color: "#64748b", margin: 0 }}>
          Upload a resume and let AI pre-fill the form for you — or skip to fill manually.
        </p>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          width: "100%",
          maxWidth: 520,
          border: `2px dashed ${isDragging ? "#01d9a9" : file ? "#014c75" : "#cbd5e1"}`,
          borderRadius: 14,
          padding: "28px 24px",
          textAlign: "center",
          background: isDragging ? "#f0fdfb" : file ? "#f8fffd" : "#fafafa",
          transition: "all 0.2s ease",
          cursor: "pointer",
          position: "relative",
        }}
      >
        <input
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
          onChange={handleBrowse}
          disabled={parsing}
        />

        {file ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            {parsed
              ? <CheckCircleFilled style={{ fontSize: 36, color: "#01d9a9" }} />
              : <FileTextOutlined style={{ fontSize: 36, color: "#014c75" }} />}
            <span style={{ fontWeight: 600, color: "#014c75", fontSize: 15 }}>{file.name}</span>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>{(file.size / 1024).toFixed(0)} KB • Click to change</span>
            {parsed && <span style={{ fontSize: 13, color: "#01d9a9", fontWeight: 600 }}>✓ Parsed successfully</span>}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <InboxOutlined style={{ fontSize: 40, color: "#cbd5e1" }} />
            <p style={{ margin: 0, fontWeight: 600, color: "#475569", fontSize: 14 }}>Drop your resume here</p>
            <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>PDF, DOC, DOCX, or TXT • up to 20 MB</p>
          </div>
        )}

        <div
          style={{
            position: "absolute",
            bottom: 10,
            right: 14,
            zIndex: 1,
            pointerEvents: "auto",
          }}
          onClick={(e) => { e.stopPropagation(); handleDownloadTemplate(); }}
        >
          <span style={{ fontSize: 12, color: "#94a3b8", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, userSelect: "none" }}>
            <DownloadOutlined style={{ fontSize: 11 }} />
            Download sample template
          </span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {file && !parsed && (
          <button
            onClick={handleParse}
            disabled={parsing}
            style={{
              height: 42,
              padding: "0 28px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #01d9a9, #00b894)",
              color: "white",
              fontWeight: 700,
              fontSize: 14,
              cursor: parsing ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 4px 12px rgba(1,217,169,0.3)",
              opacity: parsing ? 0.7 : 1,
            }}
          >
            {parsing ? <Spin indicator={<LoadingOutlined style={{ color: "white" }} spin />} size="small" /> : <RobotOutlined />}
            {parsing ? "Parsing with AI..." : "Auto-fill with AI"}
          </button>
        )}

        {file && parsed && (
          <button
            onClick={() => onParsed(null, file)}
            style={{
              height: 42,
              padding: "0 28px",
              borderRadius: 10,
              border: "1.5px solid #01d9a9",
              background: "white",
              color: "#01d9a9",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Continue →
          </button>
        )}

        <span
          onClick={() => !parsing && onSkip(file)}
          style={{
            color: "#64748b",
            fontSize: 14,
            fontWeight: 400,
            textDecoration: "underline",
            cursor: parsing ? "not-allowed" : "pointer",
            opacity: parsing ? 0.5 : 1,
            lineHeight: "42px",
          }}
        >
          {file && !parsed ? "Skip AI, continue →" : "Skip, fill manually →"}
        </span>
      </div>

      <p style={{ fontSize: 12, color: "#94a3b8", margin: 0, textAlign: "center", maxWidth: 400 }}>
        All parsed fields are editable. Original resume stays private — only you and admins can access it.
      </p>
    </div>
  );
};

export default ResumeUpload;
